// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';

// Ultra loops (≤ 1.5 MB) replay on every hover, so the last 8 stay. An open profile plays up to four larger parts: its
// effect layers (≤ 3 MB each) and its revealed ultra (intro ≤ 2 MB, loop ≤ 3 MB). Only the last two parts stay; the
// others are read again from the HTTP cache when the profile plays again. At most ~18 MB.
const MAX_LOOPS = 8;
const MAX_PROFILE_PARTS = 2;
const loops = new Map<string, Promise<Blob>>();
const profileParts = new Map<string, Promise<Blob>>();

// Opening starts only once every frame is in Fluxer's image cache , which loads each URL
// once, retries, and after a failed load tries again once its cool-down is over.
export function preloadFrontFrames(urls: ReadonlyArray<string>): Promise<void> {
	return Promise.all(
		urls.map(
			(url) =>
				new Promise<void>((resolve, reject) => {
					ImageCacheUtils.loadImage(url, resolve, () => reject(new Error(`Ultra frame ${url}`)));
				}),
		),
	).then(() => {});
}

// One download per URL; each open plays it from a new blob URL, so it restarts without the network.
// A failure stays cached: no retry storm, the layer falls back to the plain URL.
function loadOnce(cache: Map<string, Promise<Blob>>, max: number, url: string): Promise<Blob> {
	const hit = cache.get(url);
	if (hit) {
		cache.delete(url);
		cache.set(url, hit);
		return hit;
	}
	const download = fetch(url).then((response) => {
		if (!response.ok) throw new Error(`Ultra loop ${response.status}`);
		return response.blob();
	});
	cache.set(url, download);
	if (cache.size > max) cache.delete(cache.keys().next().value as string);
	return download;
}

export function loadFrontIdle(url: string): Promise<Blob> {
	return loadOnce(loops, MAX_LOOPS, url);
}

export function loadProfilePart(url: string): Promise<Blob> {
	return loadOnce(profileParts, MAX_PROFILE_PARTS, url);
}
